import { EventFeeType, Status } from '@/utils';
import moment from 'moment';
import * as yup from 'yup';

export const onlyHalfSizeRegex =
  /[\uFF01-\uFF60\uFFE0-\uFFEF\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/;
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];
const FILE_SIZE_LIMIT = 10 * 1024 * 1024;

export const formSchemaFn = (
  isActionEdit: boolean,
  isNotPermissionEdit: boolean,
) =>
  yup.object().shape({
    name: yup
      .string()
      .trim()
      .required('タイトルは未入力です。')
      .max(40, 'タイトルは40文字以内で入力してください。'),
    eventFrom: yup
      .date()
      .required('開催開始日時を選択してください。')
      .when('publicAt', ([publicAt], schema) => {
        if (!publicAt) {
          return schema;
        }
        return schema.min(
          moment(publicAt).startOf('minute').toDate(),
          '開催期間は配信日時より後の日付を選択してください。',
        );
      }),
    eventTo: yup
      .date()
      .required('開催終了日時を選択してください。')
      .transform((curr, orig) => (orig === '' ? null : curr))
      .test(
        'eventTo-min-eventFrom',
        '開催終了日時は開催開始日時より後の日付を選択してください。',
        function (value) {
          const { eventFrom } = (this as any).parent || {};
          if (!value || !eventFrom) return true;
          return moment(value).isSameOrAfter(moment(eventFrom));
        },
      )
      .test(
        'eventTo-max-publicEndAt',
        isActionEdit
          ? '開催終了日時が公開終了日時より後の日時なので、イベントを更新できませんでした。'
          : '開催終了日時が公開終了日時より後の日時なので、イベントを作成できませんでした。',
        function (value) {
          const { publicEndAt } = (this as any).parent || {};
          if (!publicEndAt || !value) return true;
          const v = moment(value).startOf('minute').toDate().getTime();
          const p = moment(publicEndAt).startOf('minute').toDate().getTime();
          return v <= p;
        },
      ),
    url: yup
      .string()
      .required('遷移先は未入力です。')
      .url('遷移先は正しい形式で入力してください。')
      .max(2000, '遷移先は2000文字以内で入力してください。'),
    content: yup
      .string()
      .required('本文は未入力です。')
      .url('URLは正しい形式で入力してください。')
      .max(500, '本文は500文字以内で入力してください。'),
    ctaText: yup
      .string()
      .required('CTA文言は未入力です。')
      .transform((value) => value?.trim())
      .max(40, 'CTA文言は40文字以内で入力してください。'),
    status: yup
      .number()
      .oneOf([0, 1], 'ステータスが不正です。')
      .required('ステータスは必須です。')
      .typeError('ステータスが不正です。'),
    publicAt: yup
      .date()
      .required('公開日時を選択してください。')
      .when(
        ['status', 'prevStatus', 'pushNotificationAt'],
        ([status], schema) => {
          if (status !== Status.PUBLIC || isActionEdit) {
            return schema;
          }
          return schema.min(
            moment().add(1, 'minute').startOf('minute').toDate(),
            '公開日時は現在以降を選択してください。',
          );
        },
      ),
    publicEndAt: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .when('publicAt', ([publicAt], schema) => {
        if (!publicAt) {
          return schema;
        }
        return schema.min(
          moment(publicAt).startOf('minute').toDate(),
          '公開終了日時は公開日時より後にしてください。',
        );
      }),
    postCodeType: yup.number().required('開催場所は未入力です。'),
    zipCode: yup
      .string()
      .when(
        ['postCodeType', 'preZipCode', 'keepCheckZipCodeError', 'address1'],
        (
          [postCodeType, preZipCode, keepCheckZipCodeError, address1],
          schema,
        ) => {
          if (!postCodeType) {
            return schema;
          }
          return schema
            .required('郵便番号は未入力です。')
            .test(
              'noFullWidth',
              '郵便番号は半角英数字で入力してください。',
              (zipCode) => {
                return !onlyHalfSizeRegex.test(zipCode || '');
              },
            )
            .test('zipCode', '「住所検索」をタップしください', (zipCode) => {
              return (
                keepCheckZipCodeError || (preZipCode === zipCode && address1)
              );
            })
            .test('errorZipCode', 'この番号では検索できません。', (_) => {
              return !keepCheckZipCodeError;
            });
        },
      ),
    attachment: yup
      .mixed()
      .when(
        ['attachments', 'imageBanner'],
        ([attachments, imageBanner], schema) => {
          if (imageBanner && (!attachments || !attachments?.fileList?.length)) {
            return schema;
          }
          if (!attachments || !attachments?.fileList?.length) {
            return schema.required('カバー画像をアップロードしてください。');
          }
          return schema
            .test('fileSize', '画像のサイズは10MBを超えています。', (_) => {
              return attachments?.fileList[0]?.size <= FILE_SIZE_LIMIT;
            })
            .test(
              'fileType',
              '画像は .jpg、.png、.jpeg 形式ではありません。',
              (_) => {
                return SUPPORTED_FORMATS.includes(
                  attachments?.fileList[0]?.type,
                );
              },
            );
        },
      ),
  } as any);
